import {
  EnumInfoDelegaTipo,
  EnumModuloDTONecessitaFirma,
  UpdateDelegaModulo,
  UpdateDelegaRequest,
  usePostFrontRichiesteIdModuliDeleghe,
} from '@framework/api-client';
import { IModuleDelegation } from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import { message } from 'antd';
import { useCallback } from 'react';
import { EditModulesDelegationsValues } from '../components/EditModulesDelegationsSchema';

function mapModuleDelegationToRequest(
  delegations: IModuleDelegation[],
): UpdateDelegaModulo[] {
  return delegations.map(d => ({
    idRichiestaModulo: d.submissionModule.id,
    deleghe: [
      {
        tipo: EnumInfoDelegaTipo.COMPILAZIONE,
        codiceFiscale: d.compilation?.fiscalCode,
      },
      {
        tipo: EnumInfoDelegaTipo.FIRMA,
        codiceFiscale: d.sign?.fiscalCode,
      },
      ...(d.view?.map(view => ({
        tipo: EnumInfoDelegaTipo.VISUALIZZAZIONE,
        codiceFiscale: view.fiscalCode,
      })) ?? []),
    ].filter(d => Boolean(d.codiceFiscale)) as UpdateDelegaRequest[],
  }));
}

export function useHandleModulesDelegationsSubmit(params: {
  submissionId: string;
  allDelegations: IModuleDelegation[];
}) {
  const { submissionId, allDelegations } = params;
  const { t } = useTranslation();
  const update = usePostFrontRichiesteIdModuliDeleghe();

  const saveModulesDelegations = useCallback(
    async (submissionId: string, data: UpdateDelegaModulo[]) => {
      await update.mutateAsync({
        id: submissionId,
        data: data,
      });
    },
    [update],
  );

  const onSubmit = useCallback(
    async (values: EditModulesDelegationsValues, modules: string[]) => {
      if (submissionId === '<unknown>') {
        console.error('Invalid submission id');
        message.error(
          t(
            'handle-modules-delegation-submit.id-richiesta-non-valido',
            'ID richiesta non valido. Riprovare.',
          ),
        );
        return;
      }

      const edited: UpdateDelegaModulo[] = modules.map(moduleId => {
        const original = allDelegations.find(
          d => d.submissionModule.id === moduleId,
        );

        const delegations: UpdateDelegaRequest[] = [];

        if (values.compilation.enable) {
          delegations.push({
            tipo: EnumInfoDelegaTipo.COMPILAZIONE,
            codiceFiscale: values.compilation.confirmUpdate
              ? values.compilation.delegate
              : original?.compilation?.fiscalCode,
          });
        }

        if (
          values.sign.enable &&
          original?.submissionModule.modulo?.modulo?.necessitaFirma !==
            EnumModuloDTONecessitaFirma.NO
        ) {
          delegations.push({
            tipo: EnumInfoDelegaTipo.FIRMA,
            codiceFiscale: values.sign.confirmUpdate
              ? values.sign.delegate
              : original?.sign?.fiscalCode,
          });
        }

        if (values.view.enable) {
          const views = values.view.confirmUpdate
            ? values.view.delegate
            : original?.view.map(v => v.fiscalCode);

          delegations.push(
            ...(views?.map(view => ({
              tipo: EnumInfoDelegaTipo.VISUALIZZAZIONE,
              codiceFiscale: view,
            })) ?? []),
          );
        }

        return {
          idRichiestaModulo: moduleId,
          deleghe: delegations.filter(d => Boolean(d.codiceFiscale)),
        };
      });

      const requestBody: UpdateDelegaModulo[] = edited.concat(
        mapModuleDelegationToRequest(
          allDelegations.filter(
            d =>
              edited.findIndex(
                r => r.idRichiestaModulo === d.submissionModule.id,
              ) === -1,
          ),
        ),
      );

      await saveModulesDelegations(submissionId, requestBody);
    },
    [allDelegations, saveModulesDelegations, submissionId],
  );

  return {
    onSubmit,
  };
}
